import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Breadcrumbs, Link, Chip, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DynamicForm from '../components/forms/DynamicForm';
import { servicesCatalog } from '../config/formsConfig';
import api from '../api/api';

const MATCHING_MESSAGES = {
  locating: 'Obteniendo tu ubicacion...',
  matching: 'Buscando prestadores disponibles...',
};

function ServiceRequestPage({ serviceKey: propKey }) {
  const { serviceKey: paramKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const serviceKey = propKey || paramKey;
  const catalogEntry = servicesCatalog.find(s => s.slug === serviceKey);
  const serviceTitle = location.state?.serviceTitle || catalogEntry?.name || serviceKey;

  // Feedback visual mientras corre geolocalizacion + matching (puede tardar ~5s)
  const [matchingStatus, setMatchingStatus] = React.useState(null); // null | 'locating' | 'matching'

  // Scroll al tope al entrar al formulario
  React.useEffect(() => { window.scrollTo(0, 0); }, [serviceKey]);

  // Pide las coordenadas del browser — silencioso si el usuario deniega
  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ lat: null, lng: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        ()    => resolve({ lat: null, lng: null }),
        { timeout: 5000 }
      );
    });

  const handleSubmitSuccess = async (serviceRequestId) => {
    console.log('[ServiceRequestPage] Solicitud creada, id:', serviceRequestId);

    if (!serviceRequestId) {
      console.warn('[ServiceRequestPage] serviceRequestId es nulo — matching omitido');
      navigate('/mis-solicitudes', { state: { fromSubmit: true, serviceTitle } });
      return;
    }

    // Paso 1: obtener coordenadas del browser
    setMatchingStatus('locating');
    const { lat, lng } = await getCoords();
    console.log('[ServiceRequestPage] Coords:', { lat, lng });

    // Paso 2: disparar el algoritmo de matching
    setMatchingStatus('matching');
    try {
      const result = await api.post(`/matching/run/${serviceRequestId}`, {
        client_lat: lat,
        client_lng: lng,
      });
      console.log('[ServiceRequestPage] Matching completado:', result.data);
    } catch (err) {
      // Falla silenciosamente — la solicitud ya fue creada correctamente
      console.warn('[ServiceRequestPage] Matching no pudo ejecutarse:', err.message);
    }

    navigate('/mis-solicitudes', {
      state: { fromSubmit: true, serviceTitle },
    });
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" variant="body2"
          onClick={() => navigate('/services')}
          underline="hover" sx={{ color: '#163244', cursor: 'pointer' }}>
          Servicios
        </Link>
        <Typography variant="body2" color="text.primary">{serviceTitle}</Typography>
      </Breadcrumbs>

      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/services')}
          sx={{ color: '#163244', minWidth: 0, px: 1 }}
        >
          Volver
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ color: '#163244' }}>
              {serviceTitle}
            </Typography>
            <Chip label="Solicitud de servicio" size="small"
              sx={{ backgroundColor: '#E7A432', color: '#163244', fontSize: '0.75rem' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Completa el formulario y te conectamos con los mejores prestadores disponibles.
          </Typography>
        </Box>
      </Box>

      {/* Feedback post-submit mientras corre matching */}
      {matchingStatus && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          mb: 3, p: 2, borderRadius: 2,
          backgroundColor: '#f0f5f8',
          border: '1px solid #cddae4',
        }}>
          <CircularProgress size={20} sx={{ color: '#163244', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#163244', fontWeight: 600 }}>
            {MATCHING_MESSAGES[matchingStatus]}
          </Typography>
        </Box>
      )}

      {/* Formulario */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
        <DynamicForm
          serviceKey={serviceKey}
          serviceTitle={serviceTitle}
          onSubmitSuccess={handleSubmitSuccess}
        />
      </Paper>
    </Box>
  );
}

export default ServiceRequestPage;
export { ServiceRequestPage };
